/* eslint-disable react-hooks/exhaustive-deps */
import { useDetermineLocale, useSDMContextProps } from '@/hooks';
import { SDMContexts } from '@ldp/sdm-contexts';
import { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { cleanUp, isPharmaprixBanner, refreshTokenFlow, saveTokens, setLastVisitedPage } from '@/utils';
import * as Sentry from '@sentry/react';
import messages, { type Locale } from 'I18n/translations';
import { usePathname } from 'next/navigation';
import { Spinner } from 'Shared/spinner';
import { QueryClientSideProvider } from './QueryClientProviders';
import { useKameleoonEngine } from '@/utils/kameleoon/use-kameleoon-engine';

interface LayoutProps {
  children: React.ReactNode;
  viewDefinitionResults?: Record<string, any> | null;
}

export default function Layout({ children, viewDefinitionResults }: LayoutProps) {
  const locale = useDetermineLocale();
  const [preferredLocale, setPreferredLocale] = useState<Locale>('en');
  const sdmContextProps = useSDMContextProps();
  const pathname = usePathname();
  const [isAuthValid, setIsAuthValid] = useState(true);
  
  useKameleoonEngine() // Kam client-side checks
  
  const refreshFlow = async () => {
    try {
      const tokens = await refreshTokenFlow();
      if (tokens !== null) {
        Sentry.captureMessage('Refresh Token successfully');
        saveTokens(tokens);
        const isUnauthorized = Number(viewDefinitionResults?.error?.status) === 401;
        if (isUnauthorized) {
          setIsAuthValid(false);
          window.location.href = pathname;
        }
      }
    } catch (error) {
      Sentry.addBreadcrumb({ message: 'Refresh flow failed on Layout' });
      Sentry.captureException(error);
    }
  };

  useEffect(() => setPreferredLocale(locale), [locale]);

  useEffect(() => setLastVisitedPage(pathname), [pathname]);

  useEffect(() => {
    const domains = isPharmaprixBanner()
      ? ['.pharmaprix.ca', process.env.NEXT_PUBLIC_COOKIE_DOMAIN_FR_URL]
      : ['.shoppersdrugmart.ca', process.env.NEXT_PUBLIC_COOKIE_DOMAIN_EN_URL];
    domains.forEach((domain) => cleanUp(domain));

    // Run refresh flow asynchronously without blocking render
    refreshFlow();
  }, []);

  const pageContent = (
      <SDMContexts {...sdmContextProps}>
        <IntlProvider
          locale={preferredLocale}
          messages={messages[preferredLocale]}
          onError={() => {
            return 'no error';
          }}
        >
          <QueryClientSideProvider>{children}</QueryClientSideProvider>
        </IntlProvider>
      </SDMContexts>
  );

  if (!isAuthValid) {
    return <Spinner />;
  }

  return pageContent;
}
